import { Link } from 'react-router-dom';

import type { PromptKind } from '../api';
import { usePromptTemplates } from '../hooks/useAdminPrompts';

interface PromptCard {
  kind: PromptKind;
  title: string;
  description: string;
}

const PROMPT_CARDS: PromptCard[] = [
  {
    kind: 'Extraction',
    title: 'Extracción de campos',
    description: 'Prompt que se envía al modelo cuando se procesa un documento contra un DocumentType publicado.',
  },
  {
    kind: 'Discovery',
    title: 'Descubrimiento de schema',
    description: 'Prompt que se envía al modelo durante el discovery — muestras OCR → propuesta de schema.',
  },
  {
    kind: 'ProviderTest',
    title: 'Test de conectividad',
    description: 'Prompt corto que usa el botón "Test" del panel para verificar que un provider responde JSON válido.',
  },
  {
    kind: 'Segmentation',
    title: 'Segmentación',
    description: 'Prompt que se envía al modelo para detectar los límites entre documentos en un fichero multi-documento.',
  },
  {
    kind: 'Reeducation',
    title: 'Reeducación',
    description: 'Prompt que se envía al modelo para proponer mejoras de schema a partir de muestras doradas de producción.',
  },
  {
    kind: 'Classification',
    title: 'Clasificación de tipo',
    description: 'Prompt que elige el tipo de documento entre los tipos publicados del tenant cuando un upload se sube en modo automático.',
  },
];

/** Lista las plantillas de prompts globales (una por PromptKind) con sus métricas resumen. */
export function PromptsListPage(): JSX.Element {
  const { data, isLoading, isError } = usePromptTemplates();
  const summariesByKind = new Map((data ?? []).map((s) => [s.kind, s] as const));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Prompts de IA</h1>
        <p className="mt-1 text-sm text-slate-500">
          Plantillas editables a nivel plataforma. Cualquier edición se aplica a todos los tenants
          en la siguiente extracción / descubrimiento / test.
        </p>
      </header>

      {isError ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          No se pudo cargar la lista de prompts.
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {PROMPT_CARDS.map((card) => {
          const summary = summariesByKind.get(card.kind);
          return (
            <Link
              key={card.kind}
              to={`/admin/prompts/${card.kind}`}
              className="card flex flex-col gap-3 p-4 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{card.title}</h2>
                  <p className="mt-1 text-xs font-mono text-slate-500">{card.kind}</p>
                </div>
                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                  {isLoading ? '…' : summary !== undefined ? `v${summary.activeVersionNumber}` : '—'}
                </span>
              </div>
              <p className="text-sm text-slate-600">{card.description}</p>
              <dl className="mt-auto grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <div>
                  <dt className="font-medium text-slate-600">Versiones</dt>
                  <dd>{summary?.totalVersions ?? '—'}</dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-600">Editado</dt>
                  <dd>{summary !== undefined ? new Date(summary.updatedAt).toLocaleString() : '—'}</dd>
                </div>
              </dl>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
